import { Link, router } from "@inertiajs/react";
import { ArrowLeft, Clock } from "lucide-react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { FichaBody } from "@/components/ficha/FichaBody";
import { serviceMetrics } from "@/components/ficha/metrics";
import type { BookingSummary, PageProps, Service } from "@/types";

interface Props extends PageProps {
  service: Service;
  analytics: BookingSummary;
  can_edit?: boolean;
  can_delete?: boolean;
}

function formatPrice(cents: number, currency: string) {
  const amount = cents / 100;
  if (currency === "MXN") return `$${amount.toLocaleString("es-MX")} MXN`;
  return `$${amount.toFixed(2)} ${currency}`;
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}

export default function ServiceShow({
  service,
  analytics,
  can_edit = true,
  can_delete = false,
  current_user,
  current_organization,
  current_business,
}: Props) {
  const orgSlug = current_organization?.slug || "";

  const handleDelete = () => {
    if (!confirm(`¿Eliminar "${service.name}"?`)) return;
    router.delete(`/${orgSlug}/services/${service.id}`);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/services`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a servicios
          </Link>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">
                {service.emoji ? `${service.emoji} ` : ""}
                {service.name}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDuration(service.duration_minutes)}
                </span>
                <span>{formatPrice(service.price_cents, service.currency)}</span>
                {!service.active && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    Inactivo
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {can_edit && (
                <Link href={`/${orgSlug}/services/${service.id}/edit`}>
                  <Button variant="outline">Editar</Button>
                </Link>
              )}
              {can_delete && (
                <Button variant="ghost" className="text-destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        <FichaBody
          analytics={analytics}
          timezone={current_business?.timezone || "America/Mexico_City"}
          metrics={serviceMetrics(
            analytics,
            current_business?.timezone || "America/Mexico_City",
          )}
          hrefFor={({ period, pastPage }) => {
            const params = new URLSearchParams();
            params.set("period", period || analytics.period);
            if (pastPage && pastPage > 1) params.set("past_page", String(pastPage));
            return `/${orgSlug}/services/${service.id}?${params.toString()}`;
          }}
        />
      </div>
    </DashboardLayout>
  );
}
